import type { AgentTodoItem, AgentTodoItemStatus, TodoState } from './types';

/** Cancelled steps are neither done nor outstanding, so they leave the denominator. */
export const COUNT_CANCELLED_STEPS: boolean = false;

export const isTerminalStatus = (status: AgentTodoItemStatus): boolean =>
  status === 'completed' || status === 'cancelled';

/**
 * A plan is finished once it has steps and every step is terminal. The
 * empty-plan case deliberately differs from {@link PlanProgress.isComplete}:
 * the dock reads an empty plan as "nothing outstanding" (and stays hidden),
 * while the card treats it as not-yet-finished (no Done badge).
 */
export const isPlanFinished = (items: readonly AgentTodoItem[]): boolean =>
  items.length > 0 && items.every((item) => isTerminalStatus(item.status));

export interface PlanProgress {
  completed: number;
  /** Steps the count is measured against (see {@link COUNT_CANCELLED_STEPS}). */
  total: number;
  currentStepTitle: string | null;
  isComplete: boolean;
}

const isCounted = (item: AgentTodoItem): boolean =>
  COUNT_CANCELLED_STEPS || item.status !== 'cancelled';

export const computePlanProgress = (state: TodoState): PlanProgress => {
  const counted = state.items.filter(isCounted);
  return {
    completed: counted.filter((item) => item.status === 'completed').length,
    total: counted.length,
    // Several steps can be in flight at once; the first in list order is the
    // one the plan reads as "where we are".
    currentStepTitle:
      state.items.find((item) => item.status === 'in_progress')?.title ?? null,
    isComplete: state.items.every((item) => isTerminalStatus(item.status)),
  };
};
